import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Loader as Loader2 } from 'lucide-react';
import PublicSite from './pages/PublicSite';
import CookieConsent from './components/CookieConsent';
import { useLocation } from 'react-router-dom';
import { installerMesureTelephone } from './lib/mesure';

const DemenagementsPage = lazy(() => import('./pages/DemenagementsPage'));
const GardeMeublesPage = lazy(() => import('./pages/GardeMeublesPage'));
const MonteMeublesPage = lazy(() => import('./pages/MonteMeublesPage'));
const EstimationVolumePage = lazy(() => import('./pages/EstimationVolumePage'));
const ActualitesPage = lazy(() => import('./pages/ActualitesPage'));
const ContactDevisPage = lazy(() => import('./pages/ContactDevisPage'));
const MentionsLegalesPage = lazy(() => import('./pages/MentionsLegalesPage'));
const ConfidentialitePage = lazy(() => import('./pages/ConfidentialitePage'));
const CgvPage = lazy(() => import('./pages/CgvPage'));
const ProtectionDonneesPage = lazy(() => import('./pages/ProtectionDonneesPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
// `AuthProvider` n'enveloppe que les routes /admin, mais il était importé
// STATIQUEMENT : il faisait donc entrer le client Supabase — et le SDK
// entier avec lui — dans le chunk d'entrée de TOUTES les pages publiques.
// Chargé en `lazy()` comme les pages qu'il enveloppe, il n'est plus
// téléchargé que par les visiteurs qui vont réellement dans le
// back-office. Il est déjà sous le <Suspense> qui entoure <Routes>.
// La couche agentique (src/agentic, ~68 Kio de source) était tirée dans le
// chunk d'entrée de toutes les pages publiques par ce seul composant. Son
// enregistrement d'outils était déjà différé à `requestIdleCallback`, mais
// le CODE, lui, était téléchargé, analysé et évalué au chargement.
//
// En `lazy()`, il part dans un chunk asynchrone. Sa logique interne n'est
// PAS touchée : le composant monte un peu plus tard, puis ses effets se
// déroulent exactement comme avant, différé compris.
//
// ⚠️ Frontière <Suspense> PROPRE, avec `fallback={null}` : sans elle, la
// suspension remonterait jusqu'au <Suspense> qui entoure <Routes> et son
// AppFallback plein écran remplacerait la page par un chargeur. Le
// composant ne rend rien de toute façon — le HTML pré-rendu est inchangé.
const WebMcpTools = lazy(() => import('./components/WebMcpTools'));
const AuthProvider = lazy(() =>
  import('./lib/AuthContext').then((m) => ({ default: m.AuthProvider })),
);
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const LoginPage = lazy(() => import('./admin/pages/LoginPage'));
const DashboardPage = lazy(() => import('./admin/pages/DashboardPage'));
const PagesListPage = lazy(() => import('./admin/pages/PagesListPage'));
const PageEditorPage = lazy(() => import('./admin/pages/PageEditorPage'));
const SitePageEditorPage = lazy(() => import('./admin/pages/SitePageEditorPage'));
const DevisListPage = lazy(() => import('./admin/pages/DevisListPage'));
const EstimationsListPage = lazy(() => import('./admin/pages/EstimationsListPage'));
const MediaPage = lazy(() => import('./admin/pages/MediaPage'));
const SettingsPage = lazy(() => import('./admin/pages/SettingsPage'));
const CommunesListPage = lazy(() => import('./admin/pages/CommunesListPage'));
const FaqListPage = lazy(() => import('./admin/pages/FaqListPage'));
const FaitsListPage = lazy(() => import('./admin/pages/FaitsListPage'));
const CommuneEditorPage = lazy(() => import('./admin/pages/CommuneEditorPage'));
const DestinationsListPage = lazy(() => import('./admin/pages/DestinationsListPage'));
const DestinationEditorPage = lazy(() => import('./admin/pages/DestinationEditorPage'));

const DemenageurLiege = lazy(() => import('./pages/satellites/DemenageurLiege'));
const DemenagementLiege = lazy(() => import('./pages/satellites/DemenagementLiege'));
const DemenagementEntreprise = lazy(() => import('./pages/satellites/DemenagementEntreprise'));
// Page prix : routée mais verrouillée tant que ses données ne sont pas
// validées — voir src/data/pages-en-preparation.ts. En production, une page
// verrouillée n'est pas pré-rendue et répond donc 404.
const PrixDemenagementPage = lazy(() => import('./pages/PrixDemenagementPage'));
// Déménagement international (lot 17) : pages pays et pages supports sous le
// pilier, un segment dynamique, même verrou.
const InternationalRoutePage = lazy(() => import('./pages/InternationalRoutePage'));
const CartonsDemenagementPage = lazy(() => import('./pages/CartonsDemenagementPage'));
// Hub changement d'adresse (lot 11) : le pilier et les fiches opérateur, même verrou.
const ChangementAdressePage = lazy(() => import('./pages/ChangementAdressePage'));
const OperateurAdressePage = lazy(() => import('./pages/OperateurAdressePage'));
// Garde-meubles par commune (lot 7) : une page, pilotée par le segment d'URL.
const GardeMeublesCommunePage = lazy(() => import('./pages/GardeMeublesCommunePage'));
const CongeDemenagementPage = lazy(() => import('./pages/CongeDemenagementPage'));
// Débarras / vide-maison (lot 9), même verrou, service à confirmer.
const DebarrasPage = lazy(() => import('./pages/DebarrasPage'));
// Garde-meubles, cas d'usage (lot 8) : trois chemins fixes, une page.
const GardeMeublesUsagePage = lazy(() => import('./pages/GardeMeublesUsagePage'));
const DemenagementPiano = lazy(() => import('./pages/satellites/DemenagementPiano'));
const DemenagementInternationalSatellite = lazy(() => import('./pages/satellites/DemenagementInternationalSatellite'));
const DemontageRemontageMeubles = lazy(() => import('./pages/satellites/DemontageRemontageMeubles'));

const GardeMeublesLiege = lazy(() => import('./pages/satellites/GardeMeublesLiege'));
const PrixGardeMeublesLiege = lazy(() => import('./pages/satellites/PrixGardeMeublesLiege'));

const ZonesInterventionPage = lazy(() => import('./pages/ZonesInterventionPage'));
const CommuneLandingPage = lazy(() => import('./pages/CommuneLandingPage'));

const ConseilsDemenagementLiege = lazy(() => import('./pages/blog/ConseilsDemenagementLiege'));
const PreparerEnfantsDemenagement = lazy(() => import('./pages/blog/PreparerEnfantsDemenagement'));
const ErreursEviterDemenagement = lazy(() => import('./pages/blog/ErreursEviterDemenagement'));

function AppFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F2EE]">
      <Loader2 className="w-8 h-8 animate-spin text-[#132073]" />
    </div>
  );
}

/**
 * N'affiche ses enfants que sur le site public.
 *
 * Le bandeau de consentement et les outils WebMCP étaient montés sur toute
 * l'application, back-office compris. Dans l'admin, le bandeau masquait le bas
 * de la navigation et demandait un consentement à un utilisateur déjà
 * authentifié — et WebMCP y exposait des outils à un agent alors que ces pages
 * ne sont pas destinées à être parcourues automatiquement.
 */
function HorsAdmin({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin')) return null;
  return <>{children}</>;
}

export default function App() {
  // Écouteur délégué des liens `tel:`, posé une fois pour tout le site.
  // `useEffect` ne s'exécute pas au pré-rendu, où `document` n'existe pas.
  useEffect(installerMesureTelephone, []);

  return (
    <BrowserRouter>
        <Suspense fallback={<AppFallback />}>
          <Routes>
            <Route path="/" element={<PublicSite />} />

            {/* Service pillar pages (new canonical routes) */}
            <Route path="/demenagement" element={<DemenagementsPage />} />
            <Route path="/garde-meubles" element={<GardeMeublesPage />} />
            <Route path="/blog" element={<ActualitesPage />} />

            {/* Legacy route redirects */}
            <Route path="/demenagements" element={<Navigate to="/demenagement" replace />} />
            <Route path="/transports" element={<Navigate to="/demenagement" replace />} />
            <Route path="/transport" element={<Navigate to="/demenagement" replace />} />
            <Route path="/actualites" element={<Navigate to="/blog" replace />} />
            <Route path="/demenagement-international" element={<Navigate to="/demenagement/demenagement-international" replace />} />
            <Route path="/monte-meubles" element={<MonteMeublesPage />} />
            <Route path="/estimation-volume" element={<EstimationVolumePage />} />

            {/* Demenagement satellite pages */}
            <Route path="/demenagement/demenageur-liege" element={<DemenageurLiege />} />
            <Route path="/demenagement/demenagement-liege" element={<DemenagementLiege />} />
            <Route path="/demenagement/demenagement-entreprise" element={<DemenagementEntreprise />} />
            <Route path="/demenagement/prix-demenagement-liege" element={<PrixDemenagementPage />} />
            <Route path="/demenagement/cartons-demenagement" element={<CartonsDemenagementPage />} />
            <Route path="/vide-maison" element={<DebarrasPage />} />
            <Route path="/changement-adresse-liege" element={<ChangementAdressePage />} />
            {/* Segment fixe déclaré avant le segment dynamique : React Router
                classe le statique au-dessus, mais l'ordre le rend lisible. */}
            <Route path="/changement-adresse-liege/conge-demenagement" element={<CongeDemenagementPage />} />
            <Route path="/changement-adresse-liege/:operateur" element={<OperateurAdressePage />} />
            <Route path="/demenagement/demenagement-piano" element={<DemenagementPiano />} />
            <Route path="/demenagement/demenagement-international" element={<DemenagementInternationalSatellite />} />
            <Route path="/demenagement/demenagement-international/:slug" element={<InternationalRoutePage />} />
            <Route path="/demenagement/demontage-remontage-meubles" element={<DemontageRemontageMeubles />} />

            {/* Transport satellite → redirigé vers international */}
            <Route path="/transport/*" element={<Navigate to="/demenagement/demenagement-international" replace />} />

            {/* Garde-meubles satellite pages */}
            <Route path="/garde-meubles/garde-meubles-liege" element={<GardeMeublesLiege />} />
            <Route path="/garde-meubles/prix-garde-meubles-liege" element={<PrixGardeMeublesLiege />} />
            <Route path="/garde-meubles/garde-meubles-etudiant" element={<GardeMeublesUsagePage />} />
            <Route path="/garde-meubles/garde-meubles-entre-deux-logements" element={<GardeMeublesUsagePage />} />
            <Route path="/garde-meubles/stockage-entreprise-archives" element={<GardeMeublesUsagePage />} />
            <Route path="/garde-meubles/:segment" element={<GardeMeublesCommunePage />} />

            {/* Blog articles */}
            <Route path="/blog/6-conseils-reussir-demenagement-liege" element={<ConseilsDemenagementLiege />} />
            <Route path="/blog/preparer-enfants-demenagement-liege" element={<PreparerEnfantsDemenagement />} />
            <Route path="/blog/6-erreurs-eviter-demenagement-liege" element={<ErreursEviterDemenagement />} />

            {/* Index des zones : page navigationnelle, laissée à la racine. */}
            <Route path="/zones-intervention" element={<ZonesInterventionPage />} />

            {/* Pages communes : /demenagement/demenagement-<slug>.
                React Router 6 n'accepte pas de segment partiellement dynamique
                (`demenagement-:slug` ne matche jamais), d'où un :slug complet
                dont le préfixe est découpé dans le composant. Les satellites
                ci-dessus restent prioritaires : un segment statique l'emporte
                toujours sur un segment dynamique dans le classement v6. */}
            <Route path="/demenagement/:slug" element={<CommuneLandingPage />} />

            {/* Contact pages */}
            <Route path="/contact-devis" element={<ContactDevisPage />} />

            {/* Legal pages */}
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            <Route path="/politique-confidentialite" element={<ConfidentialitePage />} />
            <Route path="/conditions-generales" element={<CgvPage />} />
            <Route path="/protection-donnees" element={<ProtectionDonneesPage />} />

            {/* Legacy legal routes */}
            <Route path="/confidentialite" element={<ConfidentialitePage />} />
            <Route path="/cgv" element={<CgvPage />} />

            {/* Admin.
                `AuthProvider` n'enveloppe QUE ces routes, et plus toute
                l'application.

                Monté à la racine, il posait un écouteur `onAuthStateChange` et
                appelait `getSession()` sur chacune des quatre-vingt-quinze
                pages publiques — pour un état dont aucune d'elles ne fait quoi
                que ce soit. Ce n'est pas un gain de poids : le client Supabase
                reste dans le bundle public, le formulaire de devis en a besoin.
                C'est une question de frontière — une page publique n'a pas à
                tenir un état d'authentification.

                Route de mise en page sans chemin : elle enveloppe ses enfants
                sans ajouter de segment d'URL. */}
            <Route element={<AuthProvider><Outlet /></AuthProvider>}>
              <Route path="/admin/login" element={<LoginPage />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="pages" element={<PagesListPage />} />
                <Route path="pages/:id/edit" element={<PageEditorPage />} />
                <Route path="site-pages/:id/edit" element={<SitePageEditorPage />} />
                <Route path="communes" element={<CommunesListPage />} />
                <Route path="faq" element={<FaqListPage />} />
                <Route path="faits" element={<FaitsListPage />} />
                <Route path="communes/:id/edit" element={<CommuneEditorPage />} />
                <Route path="destinations" element={<DestinationsListPage />} />
                <Route path="destinations/:slug/edit" element={<DestinationEditorPage />} />
                <Route path="devis" element={<DevisListPage />} />
                <Route path="estimations" element={<EstimationsListPage />} />
                <Route path="media" element={<MediaPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
            </Route>

            {/* Catch-all — sert la page 404 côté client. Côté serveur, Netlify
                renvoie dist/404.html avec un vrai statut HTTP 404. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <HorsAdmin>
            <CookieConsent />
            <Suspense fallback={null}>
              <WebMcpTools />
            </Suspense>
          </HorsAdmin>
        </Suspense>
    </BrowserRouter>
  );
}
